import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Radio, Select } from 'antd'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { Constants } from '@/utils'
import { dateNow } from '@/utils/DateHelpers'
import { useAuth } from '@/middlewares/auth/auth-context'
import { DEFAULT_SEARCH, parseSearchParams, useHomeSearchParams, writeSearchParams } from '@/utils/homeSearch'
import AdminOnlyContainer from '@/components/common/AdminOnlyContainer'
import { closeAdvancedSearch, useAdvancedSearchOpen } from '@/utils/advancedSearchStore'
import { COUNTRY_OPTIONS } from '@/components/common/countryOptions'

const { PERIODS, CATEGORIES } = Constants.EVENT
const ALL_COUNTRIES = DEFAULT_SEARCH.country

const ANY = ''

/** Mock `.fld-row`: label (display face, 14px, 50% ink) 6px above a 33px field select. */
function Field({ label, children }) {
    return (
        <label className="flex min-w-0 flex-col gap-1.5">
            <span className="font-display text-[14px] text-black/50">{label}</span>
            {children}
        </label>
    )
}

const KIT_BTN =
    'inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-[10px] border px-3 text-[13px] transition-colors'

/**
 * Advanced search — mock `#hsearch` (Figma "Search dropdown"): an in-flow white card under
 * the filter bar (1px --nav-bd, radius 10, padding 16, shadow 0 8px 24px 10%), a grid of four
 * labelled 33px selects (4 columns, 2 under 980px, 1 under 600px) and a footer with the
 * ghost "Reset" and the primary "Search". Focusing the nav search opens it; "Search" (or
 * Escape) closes it, "Reset" clears the filters and keeps it open (mock `$("fReset")`).
 * Filters apply to the URL as they change, like the rest of the page.
 *
 * "Event type" lists the three categories (mock `CATS`), bound to the same single-value
 * `categories` filter as the chips of the SearchBar. The former Online / Physical choice is
 * no longer offered here; its `eventType` URL param is still honoured by homeSearch.
 */
export default function AdvancedSearch() {
    const { t, i18n } = useTranslation(TranslationNS.HOME_PAGE)
    const { t: monthsT } = useTranslation(TranslationNS.MONTHS)
    const authContext = useAuth()
    const open = useAdvancedSearchOpen()

    const [searchParams, setSearchParams] = useHomeSearchParams()
    const search = parseSearchParams(searchParams)

    useEffect(() => {
        if (!open) return undefined
        const onKey = (event) => {
            if (event.key === 'Escape') closeAdvancedSearch()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open])

    const applyChange = (changes) => {
        setSearchParams((previous) => writeSearchParams(previous, { ...parseSearchParams(previous), ...changes }), {
            replace: true,
        })
    }

    const monthOptions = useMemo(
        () => [
            { value: ANY, label: t('Any month') },
            ...Array.from({ length: 12 }, (_, month) => ({
                value: String(month),
                label: monthsT(dateNow().month(month).format('MMMM')),
            })),
        ],
        [monthsT, t, i18n.resolvedLanguage],
    )

    if (!open) return null

    // Popup matches the field width: a content-sized popup overflows the viewport on mobile
    // (long country names) and gets shifted off the field; long labels ellipsize instead.
    const selectProps = { className: 'w-full' }

    return (
        <section
            aria-label={t('Advanced search')}
            data-tour="home-advanced"
            className="mx-auto mb-4 w-full px-5 md:max-w-[min(calc(100%-var(--x003)),var(--x005))] md:px-(--x001)"
        >
            <div className="grid grid-cols-1 gap-3 rounded-[10px] border border-(--nav-bd) bg-white p-4 shadow-[0_8px_24px_rgba(14,16,23,.10)] min-[601px]:grid-cols-2 min-[981px]:grid-cols-4">
                <Field label={t('Event type')}>
                    <Select
                        {...selectProps}
                        value={search.categories[0] || ANY}
                        onChange={(value) => applyChange({ categories: value ? [value] : [] })}
                        options={[
                            { value: ANY, label: t('Any type') },
                            ...CATEGORIES.map((category) => ({ value: category, label: t(category) })),
                        ]}
                    />
                </Field>
                <Field label={t('Divisions')}>
                    <Select
                        {...selectProps}
                        value={search.divisionType || ANY}
                        onChange={(value) => applyChange({ divisionType: value || undefined })}
                        options={[
                            { value: ANY, label: t('Any division') },
                            { value: 'single', label: t('Individual') },
                            { value: 'team', label: t('Teams') },
                        ]}
                    />
                </Field>
                <Field label={t('Month')}>
                    <Select
                        {...selectProps}
                        value={search.month === null ? ANY : String(search.month)}
                        onChange={(value) => applyChange({ month: value === ANY ? null : Number(value) })}
                        options={monthOptions}
                    />
                </Field>
                <Field label={t('Country')}>
                    <Select
                        {...selectProps}
                        showSearch
                        optionFilterProp="searchLabel"
                        value={search.country && search.country !== ALL_COUNTRIES ? search.country : ANY}
                        onChange={(value) => applyChange({ country: value || ALL_COUNTRIES })}
                        options={[
                            { value: ANY, label: t('Any country'), searchLabel: t('Any country') },
                            ...COUNTRY_OPTIONS,
                        ]}
                    />
                </Field>

                {/* mock .hsearch-foot: full row, actions pushed right. The registration toggle and
                    the admin period keep their place on the left (features the mock does not have). */}
                <div className="col-span-full flex flex-wrap items-center gap-3">
                    <Checkbox
                        checked={search.openRegistration}
                        onChange={() => applyChange({ openRegistration: !search.openRegistration })}
                        className="text-sm text-[#272727]"
                    >
                        {t('Opened registrations')}
                    </Checkbox>
                    {authContext.isAdmin && (
                        <AdminOnlyContainer>
                            <Radio.Group
                                options={PERIODS.map((period) => ({ label: t(period), value: period }))}
                                onChange={(event) => applyChange({ period: event.target.value })}
                                value={search.period}
                                optionType="button"
                                buttonStyle="solid"
                                size="small"
                            />
                        </AdminOnlyContainer>
                    )}
                    <div className="ml-auto flex gap-2">
                        <button
                            type="button"
                            onClick={() => applyChange(DEFAULT_SEARCH)}
                            className={`${KIT_BTN} border-(--nav-bd) bg-white text-[#272727] hover:bg-[#f7f7f9]`}
                        >
                            {t('Reset')}
                        </button>
                        <button
                            type="button"
                            onClick={closeAdvancedSearch}
                            className={`${KIT_BTN} border-[#272727] bg-[#272727] text-white hover:opacity-90`}
                        >
                            {t('Search')}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    )
}
